import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { Ltr, useTranslation } from '../i18n.js'
import { stockFlagLabel } from '../status-labels.js'
import { DataSurface } from './DataSurface.js'

/**
 * Choosing the frame being sold. §6.9 `INV-001`, §6.7 `POS-001`, `MR-05`.
 *
 * **Every frame sold at this counter stayed on the shelf.** `stock-lines.ts` relieves stock for a
 * line that names a `stockItemId` or a catalogue frame, and the only way this screen could name
 * either was a free-text field holding a UUID. Nobody types a UUID at a counter, so in practice a
 * frame line carried neither: the sale completed, the shelf was untouched, and `INV-002`'s
 * valuation and every reorder report drifted from the first sale onwards, silently.
 *
 * So this is the chooser that field needed, and it chooses the **lot** rather than the model. Two
 * lots of one frame are two different costs (`MR-05` keeps per-line margin truthful), and naming
 * the lot means the server has nothing to resolve: `stock-lines.ts` only guesses when a line names
 * a catalogue frame and the branch holds exactly one candidate, and it refuses rather than guessing
 * when there are several, which at a real shop is most of them.
 *
 * **Everything it offers is something the sale accepts.** The read behind it filters to owned stock
 * in a selling, open location at this branch with something on hand, which is the same resolution
 * the confirm performs. A chooser whose choices are refused would be worse than none, because a
 * refused write holds the head of the queue (`OFF-001a`).
 */

/** One frame a branch can sell right now, as `GET /stock/frames` gives it. */
export interface FrameOption {
  readonly stockItemId: string
  readonly frameVariantId: string
  readonly brand: string
  readonly model: string
  readonly collection?: string
  readonly colour?: string
  /** Eye, bridge and temple in millimetres, as the three numbers written on a temple. */
  readonly size?: string
  readonly lot: string
  /** Available, which is what a sale will accept. `RES-001`. */
  readonly quantity: number
  /** On hand, and how much of it is promised to somebody. Absent on an older server. */
  readonly onHand?: number
  readonly reserved?: number
  readonly flags: readonly string[]
}

export interface FrameChoiceProps {
  readonly load: (query: string) => Promise<readonly FrameOption[]>
  /** The lot already chosen on this line, so reopening the chooser says which one it is. */
  readonly chosenStockItemId?: string
  readonly onChoose: (option: FrameOption) => void
}

/**
 * The name a shopkeeper says out loud, in the order they say it.
 *
 * Takes the name parts rather than a whole `FrameOption` so the loan frame lists can call it too:
 * a loan carries the same five parts and none of the stock fields, and a second label builder
 * beside this one is how two screens end up naming one frame two ways.
 */
export type FrameNameParts = Pick<FrameOption, 'brand' | 'model' | 'collection' | 'colour' | 'size'>

export function frameName(option: FrameNameParts): string {
  return [option.brand, option.collection, option.model, option.colour, option.size]
    .filter((part) => part !== undefined && part !== '')
    .join(' ')
}

export function FrameChoice({ load, chosenStockItemId, onChoose }: FrameChoiceProps): JSX.Element {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<readonly FrameOption[] | null>(null)
  const [failed, setFailed] = useState(false)
  /** Bumped by the surface's retry, so the same query is asked again. */
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(
    (text: string) => {
      let cancelled = false
      void load(text)
        .then((loaded) => {
          if (cancelled) return
          setOptions(loaded)
          setFailed(false)
        })
        .catch(() => {
          // Named rather than degraded to an empty list. An empty chooser reads as "the shop has no
          // frames", which would send a dispenser to the free-text field and back to selling frames
          // that never leave the shelf.
          if (!cancelled) {
            setOptions([])
            setFailed(true)
          }
        })
      return () => {
        cancelled = true
      }
    },
    [load],
  )

  useEffect(() => {
    // Debounced for the reason `CustomerSearch` gives: this fires per keystroke and a Pakistani
    // shop's uplink is the constraint the product is designed around. The shelf is loaded once with
    // an empty query, so the chooser opens with something in it rather than waiting to be typed in.
    let cancel: (() => void) | undefined
    const timer = setTimeout(() => {
      cancel = reload(query)
    }, 300)
    return () => {
      clearTimeout(timer)
      cancel?.()
    }
  }, [query, reload, attempt])

  const chosen = options?.find((o) => o.stockItemId === chosenStockItemId)

  return (
    <div className="lm-frame-choice">
      <label className="lm-field">
        <span className="lm-field__label">{t('frameChoice.label')}</span>
        {/* `LOC-005`: a brand is typed in whichever script the person has, and a barcode is digits. */}
        <input
          className="lm-input"
          dir="auto"
          value={query}
          autoComplete="off"
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
      </label>

      {chosen === undefined ? null : (
        <p className="lm-frame-choice__chosen">
          {t('frameChoice.chosen', { frame: frameName(chosen), lot: chosen.lot })}
        </p>
      )}

      {/*
        The search box and the chosen-lot line stay outside the surface, so typing is never
        interrupted by a skeleton swap. `empty` and `filtered` follow the query and not the row
        count: an empty shelf asks for a delivery, an empty search asks for a different word.
      */}
      <DataSurface
        state={
          failed
            ? {
                kind: 'error',
                message: t('frameChoice.loadFailed'),
                onRetry: () => {
                  setOptions(null)
                  setFailed(false)
                  setAttempt((n) => n + 1)
                },
              }
            : options === null
              ? { kind: 'loading' }
              : options.length === 0
                ? { kind: query.trim() === '' ? 'empty' : 'filtered' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={4}
        what={t('frameChoice.what')}
        emptyMessage={t('frameChoice.empty')}
        filteredMessage={t('frameChoice.none')}
      >
        <ul className="lm-frame-choice__list">
          {(options ?? []).map((option) => (
            <li key={option.stockItemId}>
              <button
                type="button"
                className={
                  option.stockItemId === chosenStockItemId
                    ? 'lm-frame-option lm-frame-option--chosen'
                    : 'lm-frame-option'
                }
                aria-pressed={option.stockItemId === chosenStockItemId}
                onClick={() => {
                  onChoose(option)
                }}
              >
                <span className="lm-frame-option__name" dir="auto">
                  {frameName(option)}
                </span>
                <span className="lm-frame-option__meta">
                  {/* The lot and what can be sold, because the dispenser is choosing between two
                      lots of the same frame and needs to know there is one left, not six. The
                      number is available and not on-hand (`RES-001`): offering a frame the sale
                      would refuse is a picker whose choices block the queue. */}
                  {t('frameChoice.onHand', { count: option.quantity })}
                  {option.reserved === undefined || option.reserved === 0
                    ? null
                    : ` · ${t('frameChoice.held', { count: option.reserved })}`}
                  {' · '}
                  <Ltr>{option.lot}</Ltr>
                  {option.flags.length === 0
                    ? null
                    : ` · ${option.flags.map((flag) => stockFlagLabel(t, flag)).join(', ')}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </DataSurface>
    </div>
  )
}
